import { create } from 'zustand'

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info'

export interface ToastItem {
  id: string
  severity: ToastSeverity
  title: string
  description?: string
  action?: { label: string; onClick: () => void }
  duration?: number // ms, 0 = no auto-dismiss
}

type ToastInput = Omit<ToastItem, 'id'>

const MAX_TOASTS = 5

interface ToastState {
  toasts: ToastItem[]
  toast: (t: ToastInput) => string
  dismiss: (id: string) => void
  dismissAll: () => void
}

export const useToast = create<ToastState>((set) => ({
  toasts: [],

  toast: (t) => {
    const id = crypto.randomUUID()
    set((s) => ({
      toasts: [...s.toasts, { ...t, id }].slice(-MAX_TOASTS),
    }))
    return id
  },

  dismiss: (id) =>
    set((s) => ({
      toasts: s.toasts.filter((t) => t.id !== id),
    })),

  dismissAll: () => set({ toasts: [] }),
}))

/**
 * Convenience helpers for common toast severities.
 *
 * Usage:
 *   const { toast } = useToast()
 *   toast.success('Saved!')
 *
 * Or use the standalone helpers:
 *   import { toastSuccess } from '@/hooks/useToast'
 *   toastSuccess('Saved!')
 */
export function toastSuccess(title: string, opts?: Partial<ToastInput>) {
  return useToast.getState().toast({ severity: 'success', title, ...opts })
}

export function toastError(title: string, opts?: Partial<ToastInput>) {
  return useToast.getState().toast({ severity: 'error', title, ...opts })
}

export function toastWarning(title: string, opts?: Partial<ToastInput>) {
  return useToast.getState().toast({ severity: 'warning', title, ...opts })
}

export function toastInfo(title: string, opts?: Partial<ToastInput>) {
  return useToast.getState().toast({ severity: 'info', title, ...opts })
}
